import React from 'react';
import { ScriptStep } from '../services/api';
import { Info, Target, Lightbulb, Calculator, Database, Filter, ArrowRightLeft, Shield, AlertTriangle } from 'lucide-react';
import './StepCard.css';

interface StepCardProps {
  step: ScriptStep;
}

export const StepCard: React.FC<StepCardProps> = ({ step }) => {
  return (
    <div className="detail-cards-container">
      {/* What Section */}
      <div className="detail-card what">
        <div className="detail-card-content">
          <div className="detail-icon-wrapper">
            <Info className="detail-icon info" />
          </div>
          <div className="detail-text-content">
            <h4 className="detail-heading">What this step does</h4>
            <p className="detail-description">{step.details.what}</p>
          </div>
        </div>
      </div>

      {/* Why Section */}
      <div className="detail-card why">
        <div className="detail-card-content">
          <div className="detail-icon-wrapper">
            <Target className="detail-icon target" />
          </div>
          <div className="detail-text-content">
            <h4 className="detail-heading">Why it's needed</h4>
            <p className="detail-description">{step.details.why}</p>
          </div>
        </div>
      </div>

      {/* Example Section */}
      <div className="detail-card example">
        <div className="detail-card-content">
          <div className="detail-icon-wrapper">
            <Lightbulb className="detail-icon lightbulb" />
          </div>
          <div className="detail-text-content">
            <h4 className="detail-heading">Example</h4>
            <div className="example-box">
              <pre className="example-code">
                {step.details.example}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Details Section - Only show if present */}
      {step.financial_details && (
        <>
          {/* Calculations */}
          {step.financial_details.calculations && (
            <div className="detail-card calculations">
              <div className="detail-card-content">
                <div className="detail-icon-wrapper">
                  <Calculator className="detail-icon calculator" />
                </div>
                <div className="detail-text-content">
                  <h4 className="detail-heading">Calculations & Formulas</h4>
                  <div className="calculation-box">
                    <pre className="calculation-formula">{step.financial_details.calculations}</pre>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Sources & Outputs */}
          <div className="detail-card data-flow">
            <div className="detail-card-content">
              <div className="detail-icon-wrapper">
                <Database className="detail-icon database" />
              </div>
              <div className="detail-text-content">
                <h4 className="detail-heading">Data Flow</h4>
                <div className="data-flow-content">
                  {step.financial_details.data_sources && (
                    <div className="data-section">
                      <strong>Input Data:</strong>
                      <p>{step.financial_details.data_sources}</p>
                    </div>
                  )}
                  {step.financial_details.data_outputs && (
                    <div className="data-section">
                      <strong>Output Data:</strong>
                      <p>{step.financial_details.data_outputs}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Filters & Aggregations */}
          {(step.financial_details.filters || step.financial_details.aggregations) && (
            <div className="detail-card filters">
              <div className="detail-card-content">
                <div className="detail-icon-wrapper">
                  <Filter className="detail-icon filter" />
                </div>
                <div className="detail-text-content">
                  <h4 className="detail-heading">Data Processing</h4>
                  <div className="processing-content">
                    {step.financial_details.filters && (
                      <div className="data-section">
                        <strong>Filters Applied:</strong>
                        <p>{step.financial_details.filters}</p>
                      </div>
                    )}
                    {step.financial_details.aggregations && (
                      <div className="data-section">
                        <strong>Aggregations:</strong>
                        <p>{step.financial_details.aggregations}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transformations */}
          {step.financial_details.transformations && (
            <div className="detail-card transformations">
              <div className="detail-card-content">
                <div className="detail-icon-wrapper">
                  <ArrowRightLeft className="detail-icon transform" />
                </div>
                <div className="detail-text-content">
                  <h4 className="detail-heading">Data Transformations</h4>
                  <p className="detail-description">{step.financial_details.transformations}</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Validation Controls */}
      {step.validation_controls && (
        <div className="detail-card validation">
          <div className="detail-card-content">
            <div className="detail-icon-wrapper">
              <Shield className="detail-icon shield" />
            </div>
            <div className="detail-text-content">
              <h4 className="detail-heading">Validation & Controls</h4>
              <p className="detail-description">{step.validation_controls}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Handling */}
      {step.error_handling && (
        <div className="detail-card error-handling">
          <div className="detail-card-content">
            <div className="detail-icon-wrapper">
              <AlertTriangle className="detail-icon alert" />
            </div>
            <div className="detail-text-content">
              <h4 className="detail-heading">Error Handling</h4>
              <p className="detail-description">{step.error_handling}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};